import { BLOCK, INLINE } from "./constants";
import { type Block, type Inline, type Mark, type RichTextNode, type Value } from "./types";

export type MarkNode = {
  type: Mark;
};

type TextNode<T> = RichTextNode<T> & {
  type: "text";
  value: string;
  marks: MarkNode[];
};

type InlineNode<T> = RichTextNode<T> & {
  type: Inline;
  content: Array<Inline | Text>;
};

type BlockNode<T> = RichTextNode<T> & {
  type: Block;
  content: Array<Block | Inline | Text>;
};

const hasValue = (obj: Record<string, unknown>, value: unknown) =>
  Object.values(obj).includes(value);

const isText = <T>(node: RichTextNode<T> | undefined): node is TextNode<T> => node?.type === "text";

const isBlock = <T>(node: RichTextNode<T> | undefined): node is BlockNode<T> =>
  !isInline(node) && !isText(node) && hasValue(BLOCK, node?.type);

const isInline = <T>(node: RichTextNode<T> | undefined): node is InlineNode<T> =>
  hasValue(INLINE, node?.type);

export const safeJsonParse = <T>(jsonString: string): T | null => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

// This is equivalent to https://github.com/contentful/rich-text/tree/master/packages/rich-text-plain-text-renderer
export const richToPlainText = <T>(data: string, blockDivisor = " "): string => {
  const rootNode = safeJsonParse<RichTextNode<T>>(data);

  if (!rootNode || !rootNode.content) {
    return "";
  }

  return rootNode.content.reduce((acc: string, node: RichTextNode<T>, i) => {
    let nodeTextValue = "";

    if (isText(node)) {
      nodeTextValue = node.value;
    } else if (isBlock(node) || isInline(node)) {
      nodeTextValue = richToPlainText(JSON.stringify(node), blockDivisor);
      if (!nodeTextValue.length) {
        return acc;
      }
    }

    const nextNode = rootNode?.content?.[i + 1];
    const divisor = isBlock(nextNode) ? blockDivisor : "";

    return acc + nodeTextValue + divisor;
  }, "");
};

export function normalizeNode<T = Value>(node: RichTextNode<T>) {
  if (
    node.type === BLOCK.IMAGE &&
    typeof node.value === "object" &&
    node?.value &&
    "image" in node.value &&
    node?.value?.image?.url
  ) {
    return { ...node, value: { ...node.value, url: node.value.image.url } };
  }

  if (
    node.type === BLOCK.VIDEO &&
    typeof node.value === "object" &&
    node?.value &&
    "video" in node.value &&
    node.value.video?.url
  ) {
    return { ...node, value: { ...node.value, url: node.value.video.url } };
  }

  return node;
}
